import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import LocatedProperty from "@/model/LocatedProperty";
import MapBounds from "@/model/MapBounds";
import PropertyResponse from "@/model/PropertyResponse";
import { formatMoney } from "@/utils/pricing";
import "@/components/PropertiesMap/PropertiesMap.scss";

const WORLD_CENTER: [number, number] = [30, 0];
const WORLD_ZOOM = 2;
const MAX_FIT_ZOOM = 14;

interface PropertiesMapProps {
  properties: PropertyResponse[];
  highlightedId?: string | null;
  /** Query string appended to property links (e.g. the searched dates). */
  linkSearch?: string;
  /** When false the map re-fits itself to each new result set. */
  followsUser?: boolean;
  onBoundsChange?: (bounds: MapBounds) => void;
  height?: number | string;
}

function locate(properties: PropertyResponse[]): LocatedProperty[] {
  return properties.flatMap((property) => {
    const latitude = property.address?.latitude;
    const longitude = property.address?.longitude;
    return latitude !== undefined && longitude !== undefined && property.id
      ? [{ property, position: [latitude, longitude] as [number, number] }]
      : [];
  });
}

function priceIcon(label: string, highlighted: boolean): L.DivIcon {
  return L.divIcon({
    className: "properties-map__marker-wrapper",
    html: `<span class="properties-map__marker${
      highlighted ? " properties-map__marker--highlighted" : ""
    }">${label}</span>`,
    iconSize: undefined,
  });
}

interface FitToResultsProps {
  positions: [number, number][];
  enabled: boolean;
}

/** Zooms the map to the current results, unless the user is driving the search with the map. */
function FitToResults({ positions, enabled }: FitToResultsProps) {
  const map = useMap();
  const key = positions.map((position) => position.join(",")).join(";");

  useEffect(() => {
    const fit = () => {
      // Leaflet only re-measures on window resize, not when layout resizes the container.
      map.invalidateSize();
      if (enabled && positions.length > 0) {
        map.fitBounds(L.latLngBounds(positions), {
          padding: [40, 40],
          maxZoom: MAX_FIT_ZOOM,
        });
      }
    };
    fit();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(fit);
    observer.observe(map.getContainer());
    return () => observer.disconnect();
    // `key` captures the positions' content; the array itself changes identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key, enabled]);

  return null;
}

interface BoundsReporterProps {
  onBoundsChange: (bounds: MapBounds) => void;
}

function BoundsReporter({ onBoundsChange }: BoundsReporterProps) {
  const map = useMapEvents({
    moveend() {
      const bounds = map.getBounds();
      onBoundsChange({
        minLatitude: bounds.getSouth(),
        maxLatitude: bounds.getNorth(),
        minLongitude: bounds.getWest(),
        maxLongitude: bounds.getEast(),
      });
    },
  });
  return null;
}

function PropertiesMap({
  properties,
  highlightedId = null,
  linkSearch,
  followsUser = false,
  onBoundsChange,
  height = "100%",
}: PropertiesMapProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const located = useMemo(() => locate(properties), [properties]);

  return (
    <div className="properties-map" style={{ height }}>
      <MapContainer
        center={WORLD_CENTER}
        zoom={WORLD_ZOOM}
        className="properties-map__map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {located.map(({ property, position }) => {
          const highlighted = property.id === highlightedId;
          return (
            <Marker
              key={property.id}
              position={position}
              icon={priceIcon(
                formatMoney(
                  property.nightlyPriceAmount,
                  property.priceCurrency,
                  i18n.language
                ),
                highlighted
              )}
              zIndexOffset={highlighted ? 1000 : 0}
              title={property.title}
              eventHandlers={{
                click: () =>
                  navigate(
                    `/properties/${property.id}${linkSearch ? `?${linkSearch}` : ""}`
                  ),
              }}
            />
          );
        })}
        <FitToResults
          positions={located.map((item) => item.position)}
          enabled={!followsUser}
        />
        {onBoundsChange && <BoundsReporter onBoundsChange={onBoundsChange} />}
      </MapContainer>
    </div>
  );
}

export default PropertiesMap;
export type { PropertiesMapProps };
